'use client';

import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { STORY_TIMELINE } from 'lib/constants';
import SectionHeading from 'components/ui/SectionHeading';

function TimelineNode({ item, index, isLeft }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px 0px -40px 0px" });

  return (
    <div ref={ref} className="relative flex items-center justify-between md:justify-normal w-full mb-10 sm:mb-14 md:mb-16 last:mb-0 group">
      <div className="absolute left-4 sm:left-6 md:left-1/2 md:-translate-x-1/2 -translate-x-1/2 flex items-center justify-center h-full">
        <motion.div
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1 } : { scale: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="w-3.5 sm:w-4 h-3.5 sm:h-4 rounded-full bg-Mora-caramel z-10 border-2 sm:border-4 border-Mora-ivory group-hover:scale-125 transition-transform duration-500 ease-out"
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24, x: isLeft ? -16 : 16 }}
        animate={isInView ? { opacity: 1, y: 0, x: 0 } : { opacity: 0, y: 24, x: isLeft ? -16 : 16 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className={`w-[calc(100%-2.5rem)] sm:w-[calc(100%-3.5rem)] md:w-[45%] ml-8 sm:ml-12 md:ml-0 ${isLeft ? 'md:pr-12 md:text-right' : 'md:pl-12 md:ml-auto md:text-left'}`}
      >
        <span className="text-[11px] sm:text-xs uppercase tracking-widest text-Mora-caramel font-bold block mb-1.5 sm:mb-2">
          {item.label || item.year}
        </span>
        <h3 className="text-xl sm:text-2xl md:text-3xl font-heading text-Mora-dark mb-2 sm:mb-3">
          {item.title}
        </h3>
        <p className="text-Mora-brown font-body leading-relaxed text-sm sm:text-base md:text-lg">
          {item.description}
        </p>
      </motion.div>
    </div>
  );
}

export default function StoryTimeline() {
  const fallbackTimeline = [
    { label: 'The Beginning', title: 'A Nostalgic Craving', description: 'It all started with a simple craving for the warmth and comfort of home-roasted peanuts.' },
    { label: 'The Search', title: 'Seeking Perfection', description: 'We traveled across India to find the perfect peanuts, carefully selecting only the finest quality.' },
    { label: 'The Craft', title: 'Mastering the Roast', description: 'Countless hours were spent perfecting the traditional roasting process to achieve the ideal crunch and flavor.' },
    { label: 'The Birth', title: 'Mora is Born', description: 'Combining tradition with modern elegance, Mora was officially launched to share our passion.' },
    { label: 'The Future', title: 'A Legacy of Taste', description: 'Continuing to bring the authentic taste of home to modern snacking, one peanut at a time.' }
  ];

  const timelineData = STORY_TIMELINE && STORY_TIMELINE.length > 0 ? STORY_TIMELINE : fallbackTimeline;

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-Mora-ivory relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-20 max-w-5xl">
        <SectionHeading
          eyebrow="THE Mora JOURNEY"
          title="Our Journey"
          subtitle="From humble beginnings to a new standard of Indian snacking"
          className="mb-8 sm:mb-12 md:mb-14"
        />

        <div className="relative">
          <div className="absolute left-4 sm:left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-Mora-beige -translate-x-1/2" />

          <div className="relative z-10 pt-8 pb-8">
            {timelineData.slice(0, 5).map((item, index) => (
              <TimelineNode
                key={index}
                item={item}
                index={index}
                isLeft={index % 2 === 0}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
